import { Box, Stack } from "@mui/material";
import VirtualizedList from "../virtualized-list/VirtualizedList";
import { useFieldArray, useFormContext } from "react-hook-form";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useTranslation } from "react-i18next";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import NoData from "../no-data/NoData";
import { IReferenceAlarmSchemeTierDTO, IReferenceAlarmSchemeTierNotificationDTO } from "../../services/setpoint-administration-service/Models";
import { AlarmSchemeListItem } from "./AlarmSchemeListItem";
import AlarmSchemeEditorDialog, { bindAlarmSchemeEditorDialog, useAlarmSchemeEditorDialogState } from "../alarm-scheme-editor/AlarmSchemeEditorDialog";
import { BaseActions } from "../action-menu/ActionMenuItem";
import { Ref, forwardRef, useImperativeHandle } from "react";
import { IEditDTO } from "../../services/alarm-scheme-data-service/Models";
import AlarmSetpointAdministrationDataService from "../../services/setpoint-administration-service/SetpointAdministrationService";
import AlarmSchemeListUtils, { AlarmSchemeItem, AlarmSchemeListActionEnum, AlarmSchemesFormType } from "./AlarmSchemeListUtils";
import { IeLynxObject } from "../../common/models/eLynxObject";
import AlarmSetpointEditorDialog, { bindAlarmSetpointEditorDialog, useAlarmSetpointEditorDialogState } from "../alarm-setpoint-editor/AlarmSetpointEditorDialog";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useDeleteAlarmScheme } from "../../queries/alarm-scheme-admin/UseDeleteAlarmScheme";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { contains } from "../../common/utilities/Contains";

export type AlarmSchemeListType = {
    executeAction: (action: AlarmSchemeListActionEnum | BaseActions) => void;
};

export type AlarmSchemeListProps = {
    groupID: number;
    organizationID: number;
    visible?: boolean;
    viewSelected: boolean;
    filterText: string;
    onAlarmSchemeSelected?: (index: number, value: boolean) => void;
    enableSelection: boolean;
    showAvatar?: boolean;
    showSetpoints?: boolean;
    allowEdit: boolean;
    onDataChanged?: () => void;
};

function AlarmSchemeList({
    //visible,
    groupID,
    organizationID,
    viewSelected,
    filterText,
    onAlarmSchemeSelected,
    enableSelection,
    showAvatar,
    showSetpoints,
    allowEdit,
    onDataChanged
}: AlarmSchemeListProps, ref: Ref<unknown> | undefined) { 

    const {t} = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    
    const alarmSchemeEditorDialog = useAlarmSchemeEditorDialogState();
    const setpointEditorDialog = useAlarmSetpointEditorDialogState();


    const { mutate: deleteAlarmScheme } = useQueryNotifications(useDeleteAlarmScheme)({
        groupID: groupID,
        organizationID: organizationID,
        onSuccess: () => {
            acknowledge(t("deleteSuccessMessage", { itemName: t("dataTypeNames.alarmScheme") }));
            onDataChanged && onDataChanged();
        }
    });


    useImperativeHandle<unknown, AlarmSchemeListType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const {
        watch,
        control,
    } = useFormContext<AlarmSchemesFormType>();    

    const [
        alarmSchemes,
    ] = watch([
        'alarmSchemes',
    ]);

    const {
        //fields: notificationListFields,
        append: appendAlarmScheme,
        //remove:
        update: updateAlarmScheme
    } = useFieldArray({
        control: control,
        name: 'alarmSchemes',
        keyName: "fieldKey"
    });    
    
    const count = AlarmSchemeListUtils.filterAlarmSchemes(alarmSchemes, filterText).length;

    function executeAction(action: AlarmSchemeListActionEnum | BaseActions) {

        switch(action) {
            case AlarmSchemeListActionEnum.Add:
                onAddAlarmScheme();
                break;
        }
    }

    function onFilterItem(item: AlarmSchemeItem): boolean {

        let show = !!contains(item.name, filterText);
    
        if (show && viewSelected) {
            show = item.selected ?? false;
        }

        return show;
    }

    async function _editAlarmScheme(id: number) {

        return alarmSchemeEditorDialog.open({
            groupID,
            organizationID,
            alarmSchemeID: id
        });
    }

    function mapToSchemeModel(scheme: IEditDTO): AlarmSchemeItem {

        const schemeListItem: AlarmSchemeItem = {
            id: scheme.id,
            name: scheme.name,
            tiers: [],
            setpoints: []
        };

        schemeListItem.tiers = scheme.tiers.map(o => {

            const notifications = o.schedules.map(v =>{
                return {
                    notificationListID: v.notificationListID,
                    scheduleID: v.scheduleID
                } as IReferenceAlarmSchemeTierNotificationDTO
            });

            return {
                id: o.id,
                name: o.name,
                description: o.description,
                notifications: notifications,
                schedules: []

            } as IReferenceAlarmSchemeTierDTO;
        });

        return schemeListItem;
    }

    async function getUpdatedAlarmSchemeForm(scheme: IEditDTO) {

        // refreshed data is needed now, cannot wait for the query to refresh
        const data = await AlarmSetpointAdministrationDataService.getReferenceData(
            groupID, 
            organizationID)

        const schemeListItem = mapToSchemeModel(scheme);

        AlarmSchemeListUtils.updateAlarmSchemeForm(schemeListItem, 
            data
            );

        return schemeListItem;
    }


    async function onAddAlarmScheme() {
        const result = await _editAlarmScheme(-1);

        if (result.reason === "save") {

            const newScheme = result.values?.alarmScheme;

            if (newScheme) {

                const schemeListItem = await getUpdatedAlarmSchemeForm(newScheme);

                appendAlarmScheme(schemeListItem);

                onDataChanged && onDataChanged();
            }
        }
    }

    async function onEditAlarmScheme(item: AlarmSchemeItem) {

        const result = await _editAlarmScheme(item.id);

        if (result.reason === "save") {

            const updatedScheme = result.values?.alarmScheme;

            if (updatedScheme) {
                const ndx = alarmSchemes.findIndex(o => o.id === item.id);

                const schemeListItem = await getUpdatedAlarmSchemeForm(updatedScheme);

                schemeListItem.selected = item.selected;

                updateAlarmScheme(ndx, schemeListItem);

                onDataChanged && onDataChanged();
            }
        }
    }

    async function onEditSetpoint(setpointID: number) {

        const result = await setpointEditorDialog.open({
            groupID: groupID,
            setpointID: setpointID,
            organizationID: organizationID,
        });

        if (result.reason === 'save') {
            onDataChanged && onDataChanged();
        }
    }

    async function onDeleteAlarmScheme(item: AlarmSchemeItem) {

        const result = await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: t("dataTypeNames.alarmScheme") }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });
         
        if ( result === "yes") {
            deleteAlarmScheme(item.id);
        }
    }

    function onAction(action: AlarmSchemeListActionEnum, payload: AlarmSchemeItem | IeLynxObject) {

        switch (action) {
            case AlarmSchemeListActionEnum.Edit:
                onEditAlarmScheme(payload as AlarmSchemeItem);
                break;

            case AlarmSchemeListActionEnum.EditSetpoint:
                onEditSetpoint(payload.id);
                break;

            case AlarmSchemeListActionEnum.Delete:
                onDeleteAlarmScheme(payload as AlarmSchemeItem);
                break;
        }
    }

    function renderItem(_index: number, item: AlarmSchemeItem) {
        return (
            <AlarmSchemeListItem
                item={item}
                key={`${item.id}`}
                allowEdit={allowEdit}
                enableSelection={enableSelection}
                showAvatar={showAvatar}
                showSetpoints={showSetpoints}
                onAction={onAction}
                onSelected={(index, value) => onAlarmSchemeSelected && onAlarmSchemeSelected(index, value)} />
        );
    };

    return (
        <Stack
            direction='row'
            sx={{
                display: 'flex',
                flex: '1 1 auto'
            }}>
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 1 auto',
                    flexDirection: 'column',

                    //paddingTop: 1,
                    overflowY: 'auto'
                }}>

                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden"
                    }}>

                    {count === 0 &&         
                        <Box sx={{
                            padding: 1
                        }}>
                            <NoData text={t('setpointAdmin.nodata.noFilterMatch', {name: getObjectTypeName(eLynxObjectTypeEnum.AlarmScheme, true, true)})} />
                        </Box>
                    }
                                            
                    <VirtualizedList
                        items={alarmSchemes}
                        renderItem={renderItem}
                        filter={onFilterItem}
                    />

                </Box>
            </Box>
            <AlarmSchemeEditorDialog {...bindAlarmSchemeEditorDialog(alarmSchemeEditorDialog)} />
            <AlarmSetpointEditorDialog {...bindAlarmSetpointEditorDialog(setpointEditorDialog)} />
        </Stack>
    );
}

export default forwardRef(AlarmSchemeList);